<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, nextTick, ref, toRef, watch } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import type { ColumnConfig } from "@types"
import type { Services } from "@services"
import { TASK_STATE } from "@core/constants"
import { createUniqueId, formatDate } from "@core/helpers"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useTaskStore } from "@core/stores/task.store"
import type { DimensionnementPvAnalysis } from "@modules/pv/types/dimensionnement-pv.types"
import type {
    ValorisationEcoHistoryItem,
    ValorisationPvDetailResponse,
} from "@modules/pv/services/pv.service"
import type {
    PvValorisationTemplateRoot,
    ValorisationPvAnalysis,
} from "@modules/pv/types/valorisation-pv.types"
import { usePvStore } from "@modules/pv/stores/pv.store"
import { usePvPageShell } from "@modules/pv/composables/usePvPageShell"

/** Format d’affichage des dates de création (moment : `[à]` = littéral « à »). */
const CREATED_AT_DISPLAY_FORMAT = "DD/MM/YYYY [à] HH:mm"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            cardTitleDim: "Historique des études photovoltaïques",
            cardTitleEco: "Historique des études économiques",
            emptyDimTable: "Aucune analyse de dimensionnement.",
            emptyEcoTable: "Aucune analyse d’étude économique.",
            colName: "Nom",
            colCreated: "Date de réalisation",
            colDimName: "Étude de dimensionnement",
            colActions: "Actions",
            actionMenuAria: "Autres actions",
            actionModifyParams: "Modifier les paramètres",
            actionDownloadReport: "Télécharger le rapport",
            toastEcoReportDownloadingTitle:
                "Téléchargement du rapport en cours...",
            actionExportData: "Exporter les données",
            actionDelete: "Supprimer",
            confirmDeleteTitle: "Supprimer l’analyse",
            confirmDeleteDimMessage:
                "Supprimer définitivement l’analyse de dimensionnement « {name} » ?",
            confirmDeleteEcoMessage:
                "Supprimer définitivement l’étude économique « {name} » ?",
            confirmAccept: "Supprimer",
            confirmReject: "Annuler",
            toastDeleteSuccess: "Analyse supprimée",
            toastDeleteError: "La suppression a échoué",
            toastEcoPdfError: "Impossible de générer le rapport PDF",
            toastEcoPdfReady:
                "Le rapport s’ouvre dans un nouvel onglet. Si rien ne s’affiche, vérifiez que les pop-ups ne sont pas bloqués.",
            toastDimPdfError: "Impossible de lancer la génération du rapport",
            toastDimReportTaskTitle: "Téléchargement du rapport en cours...",
            toastExportError: "L’export des données a échoué",
            studyNameOpenDetail: "Voir le détail de l’étude",
            dialogClose: "Fermer",
            dialogDimTitle: "Étude de dimensionnement",
            dialogDimPlaceholder:
                "L’affichage détaillé de l’étude de dimensionnement photovoltaïque n’est pas encore disponible ici.",
            dialogEcoTitle: "Étude économique",
            toastEcoDetailError:
                "Impossible de charger le détail de l’étude économique",
            ecoDetailEmpty: "Aucune donnée d’analyse pour cette étude.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")
if (!services) {
    throw new Error("PageBuildingPVHistorique: services non injectés")
}

const {
    $generateIotAnalyseReport,
    $downloadAnalyseEco,
    $deleteAnalysePvById,
    $deleteAnalyseEcoPvById,
    $getAnalyseEcoById,
} = services

const pvStore = usePvStore()
const shell = usePvPageShell()
const isSubmittingDimensionnement = toRef(shell, "isSubmittingDimensionnement")
const buildingStore = useBuildingStore()
const taskStore = useTaskStore()
const confirm = useConfirm()
const toast = useToast()

const {
    historyEtudePv,
    historyEtudeEco,
    isFetchingDimensionnementHistoryBySource,
    isFetchingValorisationHistoryBySource,
    sourceId,
} = storeToRefs(pvStore)

const { isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

const isDeleting = ref(false)

const dimMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)
const dimMenuTarget = ref<DimensionnementPvAnalysis | null>(null)
const ecoMenuRef = ref<{ toggle: (e: Event) => void } | null>(null)
const ecoMenuTarget = ref<ValorisationEcoHistoryItem | null>(null)

/** Dialogue « étude de dimensionnement » (contenu détaillé à venir). */
const dimStudyDialogVisible = ref(false)
const dimStudyDialogTitle = ref("")

/** Dialogue « étude économique » : détail chargé depuis l’API. */
const ecoStudyDialogVisible = ref(false)
const ecoStudyDetailLoading = ref(false)
const ecoStudyAnalyse = ref<ValorisationPvAnalysis | null>(null)
const ecoStudyTemplate = ref<PvValorisationTemplateRoot | null>(null)

// ================================
// TABLEAUX
// ================================
const getDimColumns = computed<ColumnConfig[]>(() => [
    { field: "name_analyse", header: t("colName"), sortable: true },
    { field: "createdAt", header: t("colCreated"), sortable: true },
    {
        field: "dim_actions",
        header: t("colActions"),
        sortable: false,
        style: { minWidth: "3.5rem", width: "4rem" },
    },
])

const getEcoColumns = computed<ColumnConfig[]>(() => [
    { field: "name_analyse", header: t("colName"), sortable: true },
    {
        field: "name_dimensionnement",
        header: t("colDimName"),
        sortable: true,
    },
    { field: "createdAt", header: t("colCreated"), sortable: true },
    {
        field: "eco_actions",
        header: t("colActions"),
        sortable: false,
        style: { minWidth: "3.5rem", width: "4rem" },
    },
])

const getDimRows = computed(() => historyEtudePv.value ?? [])

const getEcoRows = computed(() => historyEtudeEco.value ?? [])

const getIsBuildingOrSourcesLoading = computed(
    () => isFetchingBuilding.value || isFetchingBuildingSources.value
)

const getDimTableLoading = computed(
    () =>
        getIsBuildingOrSourcesLoading.value ||
        isFetchingDimensionnementHistoryBySource.value
)

const getEcoTableLoading = computed(
    () =>
        getIsBuildingOrSourcesLoading.value ||
        isFetchingValorisationHistoryBySource.value
)

const getDimMenuItems = computed(() => {
    const row = dimMenuTarget.value
    if (!row) return []
    return [
        {
            label: t("actionModifyParams"),
            icon: "pi pi-pencil",
            command: () => {
                shell.onClickEditDimensionnement(row)
            },
        },
        {
            label: t("actionDownloadReport"),
            icon: "pi pi-download",
            command: () => {
                void onClickDimDownloadReport(row)
            },
        },
        {
            label: t("actionExportData"),
            icon: "pi pi-file-export",
            command: () => {
                void onClickDimExportData(row)
            },
        },
        {
            label: t("actionDelete"),
            icon: "pi pi-trash",
            command: () => {
                onClickDimDelete(row)
            },
        },
    ]
})

const getEcoMenuItems = computed(() => {
    const row = ecoMenuTarget.value
    if (!row) return []
    return [
        {
            label: t("actionDownloadReport"),
            icon: "pi pi-download",
            command: () => {
                void onClickEcoDownloadReport(row)
            },
        },
        {
            label: t("actionDelete"),
            icon: "pi pi-trash",
            command: () => {
                onClickEcoDelete(row)
            },
        },
    ]
})

async function openDimActionMenu(
    event: Event,
    row: DimensionnementPvAnalysis
): Promise<void> {
    dimMenuTarget.value = row
    await nextTick()
    dimMenuRef.value?.toggle(event)
}

async function openEcoActionMenu(
    event: Event,
    row: ValorisationEcoHistoryItem
): Promise<void> {
    ecoMenuTarget.value = row
    await nextTick()
    ecoMenuRef.value?.toggle(event)
}

function getCreatedAtLabel(value: string | Date | null | undefined): string {
    return formatDate(value, CREATED_AT_DISPLAY_FORMAT, "—")
}

/** Libellé de nom d’étude avec repli sur l’identifiant Mongo. */
function getStudyNameLabel(
    name: string | null | undefined,
    fallbackId: string
): string {
    return typeof name === "string" && name.length > 0 ? name : fallbackId
}

function onOpenDimStudyDialog(title: string): void {
    dimStudyDialogTitle.value = title
    dimStudyDialogVisible.value = true
}

function onClickDimStudyName(row: DimensionnementPvAnalysis): void {
    onOpenDimStudyDialog(getStudyNameLabel(row.name_analyse, row._id ?? "—"))
}

function onClickEcoDimName(row: ValorisationEcoHistoryItem): void {
    const title = getStudyNameLabel(
        row.name_dimensionnement,
        row._id_dimensionnement ?? "—"
    )
    onOpenDimStudyDialog(title)
}

async function onClickEcoStudyName(
    row: ValorisationEcoHistoryItem
): Promise<void> {
    ecoStudyDialogVisible.value = true
    ecoStudyAnalyse.value = null
    ecoStudyTemplate.value = null
    ecoStudyDetailLoading.value = true
    try {
        const detail = (await $getAnalyseEcoById(
            row._id
        )) as ValorisationPvDetailResponse
        ecoStudyAnalyse.value = detail.analyse
        ecoStudyTemplate.value =
            (detail.template as
                | PvValorisationTemplateRoot
                | null
                | undefined) ?? null
        if (!detail.analyse) {
            toast.add({
                severity: "warn",
                summary: t("dialogEcoTitle"),
                detail: t("ecoDetailEmpty"),
                life: 6000,
            })
        }
    } catch {
        toast.add({
            severity: "error",
            summary: t("toastEcoDetailError"),
            life: 6000,
        })
        ecoStudyDialogVisible.value = false
    } finally {
        ecoStudyDetailLoading.value = false
    }
}

// ================================
// CHARGEMENT
// ================================
/** `buildingId:sourceId` avec PDL par défaut si besoin — stable si `setSourceId` ne fait qu’aligner le défaut. */
const getHistoriqueLoadKey = computed((): string | null => {
    const bid = shell.getBuildingId.value
    if (!bid) return null
    if (isFetchingBuildingSources.value) return null
    if (buildingStore.hasErrorBuildingSources) return null
    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return null
    return `${bid}:${sid}`
})

/**
 * Historique des études économiques pour la source courante.
 * L’historique de dimensionnement et la dernière étude PV sont chargés par `PageBuildingPVWrapper`.
 */
async function loadHistoriqueAnalyses(): Promise<void> {
    const bid = shell.getBuildingId.value
    if (!bid) return

    if (isFetchingBuildingSources.value) {
        return
    }
    if (buildingStore.hasErrorBuildingSources) {
        return
    }

    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) {
        pvStore.resetPvDerivedContext()
        return
    }

    if (sourceId.value !== sid) {
        pvStore.setSourceId(sid, {
            fetchSourcePv: false,
        })
    }

    const ids = { sourceId: sid, buildingId: bid }
    await pvStore.fetchValorisationHistoryBySource(ids)
}

/** Rafraîchit les deux tableaux après mutation (suppression, génération rapport, etc.). */
async function refreshHistoriqueAfterMutation(): Promise<void> {
    const bid = shell.getBuildingId.value
    if (!bid) return
    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return
    const ids = { sourceId: sid, buildingId: bid }
    await Promise.all([
        pvStore.fetchDimensionnementHistoryBySource(ids),
        pvStore.fetchValorisationHistoryBySource(ids),
    ])
}

watch(
    getHistoriqueLoadKey,
    (key) => {
        if (!key) return
        void loadHistoriqueAnalyses()
    },
    { immediate: true }
)

watch(ecoStudyDialogVisible, (open) => {
    if (!open) {
        ecoStudyAnalyse.value = null
        ecoStudyTemplate.value = null
        ecoStudyDetailLoading.value = false
    }
})

watch(historyEtudeEco, (rows) => {
    if (!ecoStudyDialogVisible.value || !ecoStudyAnalyse.value?._id) return
    const id = ecoStudyAnalyse.value._id
    const stillThere = rows?.some((r) => r._id === id) ?? false
    if (!stillThere) {
        ecoStudyDialogVisible.value = false
    }
})

// ================================
// Actions — dimensionnement
// ================================
async function onClickDimDownloadReport(
    row: DimensionnementPvAnalysis
): Promise<void> {
    const rowExt = row as DimensionnementPvAnalysis & {
        url_rapport?: string
        date_expiration_rapport?: string
    }
    if (
        rowExt.url_rapport &&
        rowExt.date_expiration_rapport &&
        new Date(rowExt.date_expiration_rapport) > new Date()
    ) {
        window.location.assign(rowExt.url_rapport)
        return
    }

    try {
        const { task_id, event_name } = await $generateIotAnalyseReport(row._id)
        taskStore.followEvent(task_id, event_name, {
            taskname: "analyse.pv.dimensionnement.rapport",
            taskToastTitle: t("toastDimReportTaskTitle"),
            taskToastHideSubtitle: true,
            notification: true,
            autoDelete: true,
            onSuccess: (payload: unknown) => {
                const response = payload as {
                    data?: {
                        url_rapport?: string
                        result?: { url_rapport?: string }
                    }
                }
                const url =
                    response?.data?.url_rapport ??
                    response?.data?.result?.url_rapport
                if (typeof url === "string" && url.length > 0) {
                    window.location.assign(url)
                } else {
                    void refreshHistoriqueAfterMutation()
                }
            },
        })
    } catch {
        toast.add({
            severity: "error",
            summary: t("toastDimPdfError"),
            life: 6000,
        })
    }
}

async function onClickDimExportData(
    row: DimensionnementPvAnalysis
): Promise<void> {
    try {
        await taskStore.launchTask(
            "analyses.export_charts_excel.request",
            { _id_analyse: row._id },
            undefined,
            {
                taskname: "analyses.export_charts_excel.request",
                taskToastTitle: t("actionExportData"),
                taskToastHideSubtitle: true,
                notification: true,
                autoDelete: true,
            }
        )
    } catch {
        toast.add({
            severity: "error",
            summary: t("toastExportError"),
            life: 6000,
        })
    }
}

function onClickDimDelete(row: DimensionnementPvAnalysis): void {
    const name =
        typeof row.name_analyse === "string" && row.name_analyse.length > 0
            ? row.name_analyse
            : row._id
    confirm.require({
        header: t("confirmDeleteTitle"),
        message: t("confirmDeleteDimMessage", { name }),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("confirmReject"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("confirmAccept"),
            severity: "danger",
        },
        rejectProps: {
            label: t("confirmReject"),
            severity: "secondary",
            outlined: true,
        },
        accept: async () => {
            isDeleting.value = true
            const snap = pvStore.snapshotPvListsAndLastStudies()
            pvStore.removeAnalyseFromHistory(row._id)
            try {
                await $deleteAnalysePvById(row._id)
                toast.add({
                    severity: "success",
                    summary: t("toastDeleteSuccess"),
                    life: 4000,
                })
            } catch {
                pvStore.restorePvListsAndLastStudies(snap)
                toast.add({
                    severity: "error",
                    summary: t("toastDeleteError"),
                    life: 6000,
                })
            } finally {
                isDeleting.value = false
            }
        },
        reject: () => {},
    })
}

// ================================
// Actions — étude éco
// ================================
/** Téléchargement rapport étude éco : suivi type « tâche » (toast groupe `tasks`) pendant l’appel api-pv. */
async function onClickEcoDownloadReport(
    row: ValorisationEcoHistoryItem
): Promise<void> {
    const taskId = createUniqueId()
    taskStore.followTask(taskId, {
        state: TASK_STATE.PROGRESS,
        taskname: "pv.etude_eco.rapport_pdf",
        taskToastTitle: t("toastEcoReportDownloadingTitle"),
        notification: true,
        autoDelete: false,
    })
    taskStore.createNotifcation(taskId)

    try {
        const result = await $downloadAnalyseEco(row._id)
        const url = (result as { result?: { url?: string } })?.result?.url
        if (typeof url === "string" && url.length > 0) {
            window.open(url, "_blank", "noopener,noreferrer")
            taskStore.updateTask(taskId, {
                state: TASK_STATE.SUCCESS,
                taskToastTitle: t("actionDownloadReport"),
                taskToastSuccessMessage: t("toastEcoPdfReady"),
            })
        } else {
            const status = String(
                (result as { status?: string })?.status ?? ""
            ).trim()
            taskStore.updateTask(taskId, {
                state: TASK_STATE.FAILURE,
                taskToastTitle: t("actionDownloadReport"),
                return: status ? { message: status } : {},
                taskToastFailureMessage: t("toastEcoPdfError"),
            })
        }
    } catch {
        taskStore.updateTask(taskId, {
            state: TASK_STATE.FAILURE,
            taskToastTitle: t("actionDownloadReport"),
            taskToastFailureMessage: t("toastEcoPdfError"),
        })
    }
}

function onClickEcoDelete(row: ValorisationEcoHistoryItem): void {
    const name =
        typeof row.name_analyse === "string" && row.name_analyse.length > 0
            ? row.name_analyse
            : row._id
    confirm.require({
        header: t("confirmDeleteTitle"),
        message: t("confirmDeleteEcoMessage", { name }),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("confirmReject"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("confirmAccept"),
            severity: "danger",
        },
        rejectProps: {
            label: t("confirmReject"),
            severity: "secondary",
            outlined: true,
        },
        accept: async () => {
            isDeleting.value = true
            const snap = pvStore.snapshotPvListsAndLastStudies()
            pvStore.removeAnalyseEcoFromHistory(row._id)
            try {
                await $deleteAnalyseEcoPvById(row._id)
                toast.add({
                    severity: "success",
                    summary: t("toastDeleteSuccess"),
                    life: 4000,
                })
            } catch {
                pvStore.restorePvListsAndLastStudies(snap)
                toast.add({
                    severity: "error",
                    summary: t("toastDeleteError"),
                    life: 6000,
                })
            } finally {
                isDeleting.value = false
            }
        },
        reject: () => {},
    })
}
</script>

<template>
    <div class="page page-building-pv-historique p-0">
        <BaseTitledCard>
            <template #header>
                {{ t("cardTitleDim") }}
            </template>
            <template #content>
                <BaseTable
                    data-key="_id"
                    size="small"
                    :columns="getDimColumns"
                    :value="getDimRows"
                    :loading="getDimTableLoading"
                    :paginator="getDimRows.length > 5"
                    :rows="5"
                    search
                    :empty-message="t('emptyDimTable')"
                >
                    <template #cell_name_analyse="{ data }">
                        <BaseButton
                            v-tooltip.top="t('studyNameOpenDetail')"
                            :label="
                                getStudyNameLabel(data.name_analyse, data._id)
                            "
                            variant="link"
                            class="h-auto min-h-0 max-w-full justify-start p-0 font-normal"
                            :pt="{
                                label: 'whitespace-normal text-left break-words line-clamp-2',
                            }"
                            @click="onClickDimStudyName(data)"
                        />
                    </template>
                    <template #cell_createdAt="{ data }">
                        {{ getCreatedAtLabel(data.createdAt) }}
                    </template>
                    <template #cell_dim_actions="{ data }">
                        <BaseButton
                            v-tooltip.top="t('actionMenuAria')"
                            icon="pi pi-ellipsis-h"
                            size="small"
                            severity="secondary"
                            text
                            rounded
                            :disabled="
                                isDeleting || isSubmittingDimensionnement
                            "
                            :aria-label="t('actionMenuAria')"
                            @click="openDimActionMenu($event, data)"
                        />
                    </template>
                </BaseTable>
            </template>
        </BaseTitledCard>
        <BaseTitledCard>
            <template #header>
                {{ t("cardTitleEco") }}
            </template>
            <template #content>
                <BaseTable
                    data-key="_id"
                    size="small"
                    :columns="getEcoColumns"
                    :value="getEcoRows"
                    :loading="getEcoTableLoading"
                    :paginator="getEcoRows.length > 5"
                    :rows="5"
                    search
                    :empty-message="t('emptyEcoTable')"
                >
                    <template #cell_name_analyse="{ data }">
                        <BaseButton
                            v-tooltip.top="t('studyNameOpenDetail')"
                            :label="
                                getStudyNameLabel(data.name_analyse, data._id)
                            "
                            variant="link"
                            class="h-auto min-h-0 max-w-full justify-start p-0 font-normal"
                            :pt="{
                                label: 'whitespace-normal text-left break-words line-clamp-2',
                            }"
                            @click="onClickEcoStudyName(data)"
                        />
                    </template>
                    <template #cell_name_dimensionnement="{ data }">
                        <BaseButton
                            v-tooltip.top="t('studyNameOpenDetail')"
                            :label="
                                getStudyNameLabel(
                                    data.name_dimensionnement,
                                    data._id_dimensionnement ?? data._id
                                )
                            "
                            variant="link"
                            class="h-auto min-h-0 max-w-full justify-start p-0 font-normal"
                            :pt="{
                                label: 'whitespace-normal text-left break-words line-clamp-2',
                            }"
                            @click="onClickEcoDimName(data)"
                        />
                    </template>
                    <template #cell_createdAt="{ data }">
                        {{ getCreatedAtLabel(data.createdAt) }}
                    </template>
                    <template #cell_eco_actions="{ data }">
                        <BaseButton
                            v-tooltip.top="t('actionMenuAria')"
                            icon="pi pi-ellipsis-h"
                            size="small"
                            severity="secondary"
                            text
                            rounded
                            :disabled="isDeleting"
                            :aria-label="t('actionMenuAria')"
                            @click="openEcoActionMenu($event, data)"
                        />
                    </template>
                </BaseTable>
            </template>
        </BaseTitledCard>
        <PVTauxAutoconso v-if="getDimRows.length > 0" :scenarios="getDimRows" />

        <BaseDialog
            v-model:visible="dimStudyDialogVisible"
            modal
            dismissable-mask
            :header="t('dialogDimTitle')"
            class="w-full max-w-lg"
        >
            <p class="m-0 text-color-secondary leading-relaxed">
                {{ t("dialogDimPlaceholder") }}
            </p>
            <p
                v-if="dimStudyDialogTitle.length > 0"
                class="mt-3 font-medium text-color"
            >
                {{ dimStudyDialogTitle }}
            </p>
            <div class="mt-4 flex justify-end">
                <BaseButton
                    :label="t('dialogClose')"
                    severity="secondary"
                    @click="dimStudyDialogVisible = false"
                />
            </div>
        </BaseDialog>

        <BaseDialog
            v-model:visible="ecoStudyDialogVisible"
            modal
            dismissable-mask
            :header="t('dialogEcoTitle')"
            class="w-full max-w-5xl"
            :pt="{
                content: { class: 'max-h-[min(85vh,48rem)] overflow-y-auto' },
            }"
        >
            <BaseSkeleton
                v-if="ecoStudyDetailLoading"
                height="20rem"
                class="w-full rounded-md"
            />
            <BaseMessage
                v-else-if="!ecoStudyAnalyse"
                severity="info"
                :closable="false"
                class="w-full"
            >
                {{ t("ecoDetailEmpty") }}
            </BaseMessage>
            <PvEtudeEcoDisplay
                v-else
                :analyse="ecoStudyAnalyse"
                :eco-template="ecoStudyTemplate ?? undefined"
                :building-id="shell.getBuildingId"
                :source-id="sourceId"
            />
            <div class="mt-4 flex justify-end">
                <BaseButton
                    :label="t('dialogClose')"
                    severity="secondary"
                    @click="ecoStudyDialogVisible = false"
                />
            </div>
        </BaseDialog>

        <BaseMenu ref="dimMenuRef" :model="getDimMenuItems" popup />
        <BaseMenu ref="ecoMenuRef" :model="getEcoMenuItems" popup />
    </div>
</template>
